import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { Fragment, useState } from "react";
import { api, post } from "../../lib/api";
import { useCan } from "../../lib/session";
import { Card, Empty, ErrorNote, fmtDate, PageHeader } from "../../components/ui";

interface AuditRow {
  seq: number;
  occurredAt: string;
  actorLabel: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  before: unknown;
  after: unknown;
  source: string;
  correlationId: string | null;
  reason: string | null;
}

export function AuditPage() {
  const [filters, setFilters] = useState({ action: "", entityType: "", correlationId: "" });
  const [open, setOpen] = useState<number | null>(null);
  const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
  const audit = useQuery({ queryKey: ["audit", qs], queryFn: () => api<{ items: AuditRow[] }>(`/api/audit?limit=100&${qs}`) });
  const verify = useMutation({ mutationFn: () => api<{ ok: boolean; checked: number; brokenAt?: number }>("/api/audit/verify") });
  return (
    <div>
      <PageHeader
        title="Audit log"
        subtitle="Append-only and hash-chained: any tampering breaks the chain."
        actions={<button className="btn" onClick={() => verify.mutate()} disabled={verify.isPending}><ShieldCheck size={16} /> Verify chain</button>}
      />
      {verify.data && (
        <p className={`mb-3 text-sm ${verify.data.ok ? "text-emerald-600" : "text-red-600"}`}>
          {verify.data.ok ? `Chain intact — ${verify.data.checked} events verified.` : `Chain BROKEN at event #${verify.data.brokenAt}.`}
        </p>
      )}
      <div className="mb-3 grid gap-2 sm:grid-cols-3">
        <input className="input" placeholder="Action starts with… (e.g. authz.)" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} />
        <input className="input" placeholder="Entity type (person, role_grant…)" value={filters.entityType} onChange={(e) => setFilters({ ...filters, entityType: e.target.value })} />
        <input className="input" placeholder="Correlation id" value={filters.correlationId} onChange={(e) => setFilters({ ...filters, correlationId: e.target.value })} />
      </div>
      <Card>
        <ErrorNote error={audit.error} />
        {audit.isLoading ? <Empty>Loading…</Empty> : audit.data?.items.length ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead><tr><th>#</th><th>When</th><th>Who</th><th>Action</th><th>Entity</th><th>Source</th></tr></thead>
              <tbody>
                {audit.data.items.map((r) => (
                  <Fragment key={r.seq}>
                    <tr className="cursor-pointer hover:bg-black/5 dark:hover:bg-white/5" onClick={() => setOpen(open === r.seq ? null : r.seq)}>
                      <td className="muted">{r.seq}</td>
                      <td className="whitespace-nowrap">{fmtDate(r.occurredAt)}</td>
                      <td>{r.actorLabel}</td>
                      <td className="font-mono text-xs">{r.action}</td>
                      <td className="muted text-xs">{r.entityType}{r.entityId ? ` · ${r.entityId.slice(0, 8)}` : ""}</td>
                      <td><span className="chip">{r.source}</span></td>
                    </tr>
                    {open === r.seq && (
                      <tr>
                        <td colSpan={6}>
                          <div className="grid gap-3 text-xs md:grid-cols-2">
                            <div><div className="muted mb-1">Before</div><pre className="overflow-auto rounded bg-black/5 p-2 dark:bg-white/5">{JSON.stringify(r.before, null, 2)}</pre></div>
                            <div><div className="muted mb-1">After</div><pre className="overflow-auto rounded bg-black/5 p-2 dark:bg-white/5">{JSON.stringify(r.after, null, 2)}</pre></div>
                          </div>
                          <p className="muted mt-2 text-xs">Correlation: {r.correlationId}{r.reason ? ` · Reason: ${r.reason}` : ""}</p>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>No audit events match.</Empty>}
      </Card>
    </div>
  );
}

interface OutboxRow { id: string; type: string; subject: string | null; createdAt: string; publishedAt: string | null; attempts: number; lastError: string | null; parked: boolean }

export function EventsPage() {
  const [status, setStatus] = useState("pending");
  const can = useCan();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["outbox", status], queryFn: () => api<{ items: OutboxRow[]; stats: { pending: number; parked: number; published: number } }>(`/api/events/outbox?status=${status}`), refetchInterval: 10_000 });
  const act = useMutation({ mutationFn: ({ id, op }: { id: string; op: "retry" | "park" }) => post(`/api/events/outbox/${id}/${op}`, {}), onSuccess: () => void qc.invalidateQueries({ queryKey: ["outbox"] }) });
  return (
    <div>
      <PageHeader title="Events" subtitle="Messages ONE sends to the other apps, and their delivery status." />
      {q.data && (
        <div className="tabs mb-4">
          {(["pending", "failing", "parked", "published"] as const).map((s) => (
            <button key={s} className="tab capitalize" aria-selected={status === s} onClick={() => setStatus(s)}>
              {s}{s !== "failing" && <span className="count num">{q.data.stats[s]}</span>}
            </button>
          ))}
        </div>
      )}
      <Card>
        <ErrorNote error={q.error ?? act.error} />
        {q.data?.items.length ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead><tr><th>Type</th><th>Created</th><th>Published</th><th>Attempts</th><th>Error</th><th /></tr></thead>
              <tbody>
                {q.data.items.map((e) => (
                  <tr key={e.id}>
                    <td className="font-mono text-xs">{e.type}</td>
                    <td className="muted">{fmtDate(e.createdAt)}</td>
                    <td className="muted">{fmtDate(e.publishedAt)}</td>
                    <td>{e.attempts}</td>
                    <td className="max-w-xs truncate text-xs text-red-600">{e.lastError}</td>
                    <td className="whitespace-nowrap text-right">
                      {can("events.outbox.manage") && !e.publishedAt && (
                        <>
                          <button className="btn !min-h-0 !py-1 text-xs" onClick={() => act.mutate({ id: e.id, op: "retry" })}>Retry</button>{" "}
                          {!e.parked && <button className="btn !min-h-0 !py-1 text-xs" onClick={() => act.mutate({ id: e.id, op: "park" })}>Park</button>}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>Nothing here.</Empty>}
      </Card>
    </div>
  );
}
